import {
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Popover,
  PopoverProps,
} from "@material-ui/core";
import { AssignmentDTO } from "groopit-js/client";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getCreatorDescription, getDateDescription } from "../FilterStrings";
import { PeoplePicker } from "../PeoplePicker";
import { DatePicker } from "../DatePicker";
import { ComponentFilter } from "./ComponentFilter";
import {
  createFilterAddAssignmentAction,
  createFilterAddDateRangeAction,
  createFilterExcludeComponentFromAIAction,
  createFilterIncludeComponentInAIAction,
  createFilterRemoveAssignmentAction,
  createFilterRemoveDateRangeAction,
  createFilterReplaceCreatorsAction,
} from "../State/FilterActions";
import {
  isAssignmentFilterEmpty,
  isComponentExcludedFromAssignmentFilter,
} from "../GroopitFilterDTO";
import { StaleComponentFilter } from "./StaleComponentFilter";
import { useGroupContext } from "groopit-js";
import { useFilterContext } from "../State/FilterContext";
import { useFilterDispatchContext } from "../State/FilterDispatchContext";

const FilterPickerPopover = (props: PopoverProps) => {
  return (
    <Popover
      {...props}
      anchorOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      PaperProps={{
        style: { width: "20rem" },
      }}
    >
      {props.children}
    </Popover>
  );
};

interface AssignmentFilterProps {
  assignment: AssignmentDTO;
}

export const AssignmentFilter = (props: AssignmentFilterProps) => {
  const { assignment } = props;
  const { filter } = useFilterContext();
  const { filterDispatcher } = useFilterDispatchContext();
  const { owner } = useGroupContext();

  const assignmentFilter = useMemo(
    () => filter.filter.assignments.find((a) => a.id === assignment.id),
    [assignment.id, filter.filter.assignments],
  );
  const [isAssignmentSelected, setAssignmentSelected] = useState(
    Boolean(assignmentFilter),
  );
  useEffect(() => {
    setAssignmentSelected(Boolean(assignmentFilter));
  }, [assignmentFilter]);

  const [isExpanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!isAssignmentSelected) {
      setExpanded(false);
      setSelectedCreators([]);
      setSelectedDateRange(undefined);
    }
  }, [isAssignmentSelected]);

  const peoplePickerAnchor = useRef<HTMLSpanElement>(null);
  const [displayPeoplePicker, setDisplayPeoplePicker] = useState(false);
  const [selectedCreators, setSelectedCreators] = useState(
    assignmentFilter?.creators ?? [],
  );
  useEffect(() => {
    setSelectedCreators(assignmentFilter?.creators ?? []);
  }, [assignmentFilter?.creators]);

  const datePickerAnchor = useRef<HTMLSpanElement>(null);
  const [displayDatePicker, setDisplayDatePicker] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState(
    assignmentFilter?.dateRange,
  );
  useEffect(() => {
    setSelectedDateRange(assignmentFilter?.dateRange);
  }, [assignmentFilter?.dateRange]);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // We don't update the filter on each change because it updates
  // the underyling sentence structure dynamically which causes
  // the popup window to shift around so we track the selections
  // internally and then apply them to the filter as soon as the
  // popup window closes.
  const updateFilter = useCallback(() => {
    filterDispatcher(
      createFilterReplaceCreatorsAction(selectedCreators, assignment),
    );
    if (selectedDateRange) {
      filterDispatcher(
        createFilterAddDateRangeAction(selectedDateRange, assignment),
      );
    } else {
      filterDispatcher(createFilterRemoveDateRangeAction(assignment.id));
    }
  }, [assignment, filterDispatcher, selectedCreators, selectedDateRange]);

  const toggleAssignmentSelection = useCallback(() => {
    if (isAssignmentSelected) {
      if (!isAssignmentFilterEmpty(assignmentFilter, assignment)) {
        setShowDeleteConfirmation(true);
      } else {
        filterDispatcher(createFilterRemoveAssignmentAction(assignment.id));
      }
    } else {
      filterDispatcher(createFilterAddAssignmentAction(assignment));
    }
  }, [assignment, assignmentFilter, filterDispatcher, isAssignmentSelected]);

  const toggleExcludeComponentFromAI = useCallback(
    (componentModel_Id: number, isSelected: boolean) => {
      if (isSelected) {
        filterDispatcher(
          createFilterExcludeComponentFromAIAction(
            componentModel_Id,
            assignment,
          ),
        );
      } else {
        filterDispatcher(
          createFilterIncludeComponentInAIAction(
            componentModel_Id,
            assignment.id,
          ),
        );
      }
    },
    [assignment, filterDispatcher],
  );

  return (
    <div>
      <div
        className="flex flex-row items-center space-x-3 point-on-hover p-2 hover:bg-app-gray-12"
        onClick={() => {
          setExpanded(!isExpanded);
        }}
      >
        <img
          src={assignment.assignmentPhotoUrl}
          className="assignment-img-small"
          alt="Data model icon"
        />
        <div className="flex-grow">
          <div className="font-bold text-color-secondary">
            {assignment.title}
          </div>
        </div>
        <Checkbox
          color="primary"
          checked={isAssignmentSelected}
          onClick={(event) => event.stopPropagation()}
          onChange={toggleAssignmentSelection}
        />
      </div>
      <Collapse in={isExpanded}>
        <div className="p-4 space-y-2">
          <h3 className="text-normal">Data analysis</h3>
          <p className="text-sm">
            The following fields will be included in the analysis:
          </p>
          <div className="flex flex-wrap gap-2">
            {assignment.components
              .filter((c) => owner || !c.hiddenFromMembers)
              .map((component) => {
                const isSelected = !isComponentExcludedFromAssignmentFilter(
                  assignmentFilter,
                  component.componentModel_Id,
                  component.parentModelComponentModel_Id,
                );
                return (
                  <Chip
                    key={component.componentModel_Id}
                    role="checkbox"
                    aria-checked={isSelected}
                    aria-labelledby={`${component.componentModel_Id}_ai_toggle`}
                    color={isSelected ? "secondary" : "default"}
                    className="max-w-full"
                    label={
                      <span id={`${component.componentModel_Id}_ai_toggle`}>
                        {component.description}
                      </span>
                    }
                    onClick={() =>
                      toggleExcludeComponentFromAI(
                        component.componentModel_Id,
                        isSelected,
                      )
                    }
                  />
                );
              })}
          </div>
        </div>
        <div className="p-4 border-t border-app-gray-08">
          <h3 className="text-normal">Data filters</h3>
        </div>
        <div className="bg-app-teal-x-light text-sm px-4 py-1 italic">
          Include data from{" "}
          <span
            ref={peoplePickerAnchor}
            className={`underline point-on-hover ${
              assignmentFilter?.creators?.length ? "text-app-teal" : ""
            }`}
            onClick={() => setDisplayPeoplePicker(true)}
          >
            {getCreatorDescription(assignmentFilter?.creators ?? [])}
          </span>{" "}
          for{" "}
          <span
            ref={datePickerAnchor}
            className={`underline point-on-hover ${
              assignmentFilter?.dateRange ? "text-app-teal" : ""
            }`}
            onClick={() => setDisplayDatePicker(true)}
          >
            {getDateDescription(assignmentFilter?.dateRange)}
          </span>
          .
        </div>
        <FilterPickerPopover
          open={displayPeoplePicker}
          onClose={() => {
            setDisplayPeoplePicker(false);
            updateFilter();
          }}
          anchorEl={peoplePickerAnchor.current}
        >
          <PeoplePicker
            variant="inlined"
            selectedPeople={selectedCreators}
            onChange={setSelectedCreators}
          />
        </FilterPickerPopover>
        <FilterPickerPopover
          open={displayDatePicker}
          onClose={() => {
            setDisplayDatePicker(false);
            updateFilter();
          }}
          anchorEl={datePickerAnchor.current}
        >
          <DatePicker
            variant="inlined"
            selectedOption={selectedDateRange}
            onChange={setSelectedDateRange}
          />
        </FilterPickerPopover>
        <div className="border-t border-b border-app-gray-08">
          {assignment.components
            .filter((component) => !component.parentModelComponentModel_Id)
            .filter((component) => owner || !component.hiddenFromMembers)
            .map((component) => (
              <Fragment key={component.componentModel_Id}>
                <hr className="border-app-gray-08 m-0" />
                <ComponentFilter
                  assignment={assignment}
                  component={component}
                />
              </Fragment>
            ))}
          {assignmentFilter?.components &&
            assignmentFilter.components
              .filter(
                (componentFilter) =>
                  !assignment.components.find(
                    (c) =>
                      c.componentModel_Id === componentFilter.componentModelId,
                  ),
              )
              .map((componentFilter) => (
                <Fragment key={componentFilter.componentModelId}>
                  <hr className="border-app-gray-08 m-0" />
                  <StaleComponentFilter
                    filter={componentFilter}
                    assignmentId={assignment.id}
                  />
                </Fragment>
              ))}
        </div>
      </Collapse>
      <Dialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        aria-labelledby="delete-confirmation-title"
        aria-describedby="delete-confirmation-description"
      >
        <DialogTitle id="delete-confirmation-title">
          Remove data model filter
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-confirmation-description">
            <span className="font-bold text-app-orange">Note:</span> You have
            set filters for this data model. Deselecting the data model will
            remove all applied filters. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="default"
            variant="outlined"
            onClick={(event) => {
              setShowDeleteConfirmation(false);
              event.stopPropagation();
            }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={(event) => {
              filterDispatcher(
                createFilterRemoveAssignmentAction(assignment.id),
              );
              setShowDeleteConfirmation(false);
              event.stopPropagation();
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
